import { useRef, useState } from 'react';
import type { JSX, KeyboardEvent } from 'react';
import { Button, Modal } from '@taki/ui';
import { gesteDuClavier } from './clavier-creneau.js';
import { FormulaireCreneau, parseClock } from './FormulaireCreneau.js';
import type { FormulaireCreneauProps } from './FormulaireCreneau.js';
import { choixDuCouple, coupleAEnregistrer, entreesProjetTache } from './choix-tache.js';
import type { OptionDeProjet, OptionDeTache } from './choix-tache.js';
export type { OptionDeProjet, OptionDeTache } from './choix-tache.js';
import type { ChampAChoix } from '../catalogues.js';
import { PAS_MINUTES, clock } from './journal.js';
import type { CreneauSemaine } from './journal.js';

/**
 * L'EDITEUR D'UN CRENEAU DU JOURNAL : une fenetre standard, jamais un panneau
 * maison (`Modal` de `@taki/ui`).
 *
 * Une saisie porte une tache, et son projet se deduit d'elle. Le selecteur
 * cherche sur les deux noms et ne propose jamais de projet seul. Les champs a
 * choix du jeu viennent ensuite, puis le jour, les heures et la note. La tache
 * n'est exigee qu'a la CREATION (14/10/2026) : un ancien creneau sans tache se
 * modifie sans en choisir une, et garde son projet.
 *
 * IL NE PARLE PAS AU SDK, VOLONTAIREMENT. Il rend une SAISIE (des dates pures
 * et des minutes) et la surface se charge de l'ecrire : l'editeur reste
 * verifiable en lisant ses champs, et l'etat d'une ecriture (en cours, ratee,
 * annulable) vit a un seul endroit, le pied de la surface.
 *
 * EN CONSULTATION (l'agenda d'un collegue), tout est en lecture et le pied se
 * reduit a « Fermer » : ni enregistrement, ni suppression.
 */

/** Ce que l'editeur rend a la surface : des dates pures, deja validees. */
export interface SaisieDeCreneau {
  /** L'id de la fiche modifiee, `null` pour une creation. */
  id: string | null;
  /** Le jour choisi, date pure `YYYY-MM-DD`. */
  jour: string;
  /** Le debut, en minutes depuis minuit, cale sur le pas. */
  debutMinutes: number;
  /** La fin, meme unite, toujours au moins un pas apres le debut. */
  finMinutes: number;
  /** Le projet de la tache choisie. */
  projectId: string;
  /** La tache obligatoire sur laquelle encoder le temps. */
  taskId: string;
  /**
   * LES CHOIX SAISIS, par clef de champ (`{ categorie: 'tournage' }` chez une
   * agence). La fenetre ne connait aucune clef, elle rend ce que le jeu
   * declare.
   */
  choix: Record<string, string>;
  /** La note, chaine vide quand elle a ete videe. */
  notes: string;
}

export interface EventEditorProps {
  /** Le creneau ouvert, `null` pour une creation. */
  creneau: CreneauSemaine | null;
  /** Le jour propose a l'ouverture, date pure `YYYY-MM-DD`. */
  jourInitial: string;
  /**
   * L'HEURE DE DEBUT PROPOSEE a une creation, en minutes depuis minuit : celle
   * du double-clic sur la grille. Absente, la creation part de l'heure qu'il est.
   */
  debutPropose?: number | null;
  /** Le catalogue des projets, deja etiquete « client · projet ». */
  projets: readonly OptionDeProjet[];
  /** Les taches de ces projets (taches de Projets, famille `tasks`). */
  taches: readonly OptionDeTache[];
  /**
   * L'ORDRE DES ETAPES D'UN PROJET (les valeurs du champ `etat`, dans l'ordre
   * declare) : le choix d'une tache range par lui les projets qui ne sont pas
   * en cours (`comparerProjets`). Vide, ils se rangent par date seulement.
   */
  ordreDesEtats?: readonly string[];
  /**
   * LA CLEF DU CHAMP A CHOIX QUI TITRE UNE SAISIE, quand une org en declare
   * un : il devient obligatoire dans la fenetre. `null` sur le modele, ou la
   * saisie se titre par sa tache (14/10/2026).
   */
  clefDuTitre: string | null;
  /**
   * LES CHAMPS A CHOIX DU JEU, tels que la declaration EFFECTIVE les porte
   * (`catalogues.ts`) : ceux de l'org quand elle en tient une, ceux du bundle
   * sinon. Ils arrivent en PROP plutot que d'etre lus ici, pour que cet editeur
   * reste un composant sans contexte, montable dans un test.
   */
  choixDuJeu: readonly ChampAChoix[];
  /** L'agenda d'un collegue : tout est en lecture. */
  readOnly: boolean;
  /** Une ecriture est deja en cours : les deux gestes qui ecrivent attendent. */
  occupe: boolean;
  onClose: () => void;
  onSave: (saisie: SaisieDeCreneau) => void;
  /** Supprimer : absent sur un nouveau creneau et en consultation. */
  onDelete: (() => void) | null;
}

export function EventEditor({
  creneau,
  jourInitial,
  debutPropose = null,
  projets,
  taches,
  ordreDesEtats = [],
  clefDuTitre,
  choixDuJeu,
  readOnly,
  occupe,
  onClose,
  onSave,
  onDelete,
}: EventEditorProps): JSX.Element {
  const [now] = useState(() => new Date());
  const [projetTache, setProjetTache] = useState(() =>
    choixDuCouple(creneau?.taskId ?? ''),
  );
  const optionsTaches = entreesProjetTache(projets, taches, ordreDesEtats);
  const [choix, setChoix] = useState<Record<string, string>>(() => ({ ...(creneau?.choix ?? {}) }));
  const [jour, setJour] = useState(jourInitial);
  const debutInitial =
    creneau == null && debutPropose != null
      ? Math.max(0, Math.min(1440 - PAS_MINUTES, debutPropose))
      : creneau == null
      ? Math.min(
          1440 - PAS_MINUTES,
          Math.round((now.getHours() * 60 + now.getMinutes()) / PAS_MINUTES) * PAS_MINUTES,
        )
      : Math.round(creneau.start * 60);
  const [debut, setDebut] = useState(clock(debutInitial));
  const [fin, setFin] = useState(
    clock(
      creneau == null
        ? Math.min(1440, debutInitial + 60)
        : Math.round((creneau.start + creneau.duration) * 60),
    ),
  );
  const [notes, setNotes] = useState(creneau?.note ?? '');
  const [error, setError] = useState('');

  const debutMinutes = parseClock(debut);
  const finMinutes = parseClock(fin);
  const duree = debutMinutes != null && finMinutes != null ? finMinutes - debutMinutes : null;
  const maintenant = (): void => {
    const heure = new Date();
    const minutes = Math.min(1440 - PAS_MINUTES,
      Math.round((heure.getHours() * 60 + heure.getMinutes()) / PAS_MINUTES) * PAS_MINUTES);
    setDebut(clock(minutes));
    setFin(clock(Math.min(1440, minutes + (duree != null && duree >= PAS_MINUTES ? duree : 60))));
    setError('');
  };

  const enregistrer = (): void => {
    if (readOnly || occupe) return;
    const couple = coupleAEnregistrer(projetTache, taches, creneau);
    if (couple === null) {
      setError('Choisir une tâche liée à un projet.');
      return;
    }
    const champDuTitre = choixDuJeu.find((champ) => champ.key === clefDuTitre);
    if (champDuTitre !== undefined && (choix[champDuTitre.key] ?? '') === '') {
      setError(`Choisir : ${champDuTitre.label}.`);
      return;
    }
    const choisi = new Date(`${jour}T12:00:00`);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(jour) || !Number.isFinite(choisi.getTime())) {
      setError('Choisir un jour valide.');
      return;
    }
    const de = parseClock(debut);
    const a = parseClock(fin);
    if (de == null || a == null || de >= 1440 || de % PAS_MINUTES !== 0 || a % PAS_MINUTES !== 0) {
      setError('Les horaires doivent suivre un pas de 5 minutes.');
      return;
    }
    if (a - de < PAS_MINUTES) {
      setError('La fin doit être au moins 5 minutes après le début.');
      return;
    }
    onSave({
      id: creneau?.id ?? null,
      jour,
      debutMinutes: de,
      finMinutes: a,
      ...couple,
      choix,
      notes: notes.trim(),
    });
  };

  /* LE CLAVIER DE LA FENETRE (`clavier-creneau.ts`) : Entree valide, Tab
     depuis les notes va au bouton qui valide. Ecoute sur le corps seulement :
     dans le pied, Entree sur « Annuler » annule, comme tout bouton. */
  const valider = useRef<HTMLButtonElement>(null);
  const auClavier = (event: KeyboardEvent<HTMLDivElement>): void => {
    const geste = gesteDuClavier({
      key: event.key,
      shiftKey: event.shiftKey,
      dansLesNotes: event.target instanceof HTMLTextAreaElement,
      dejaPrise: event.isDefaultPrevented(),
      composition: event.nativeEvent.isComposing,
      lecture: readOnly,
    });
    if (geste === null) return;
    event.preventDefault();
    if (geste === 'vers-validation') valider.current?.focus();
    else enregistrer();
  };

  const formulaire: FormulaireCreneauProps = {
    options: optionsTaches, tache: projetTache, jour, debut, fin, notes, choix, choixDuJeu, readOnly,
    onTache: (value) => { setProjetTache(value); setError(''); },
    onJour: (value) => { setJour(value); setError(''); },
    onDebut: (value) => { setDebut(value); setError(''); },
    onFin: (value) => { setFin(value); setError(''); },
    onNotes: setNotes,
    onChoix: (key, value) => { setChoix((actuels) => ({ ...actuels, [key]: value })); setError(''); },
    onNow: maintenant,
    onNowFin: () => {
      const heure = new Date();
      const minutes = Math.min(1440,
        Math.round((heure.getHours() * 60 + heure.getMinutes()) / PAS_MINUTES) * PAS_MINUTES);
      setFin(clock(minutes));
      setError('');
    },
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={readOnly ? 'Créneau' : creneau == null ? 'Nouveau créneau' : 'Modifier le créneau'}
      size="md"
      footer={<>
        {!readOnly && onDelete != null && <Button variant="secondary" size="sm" aria-disabled={occupe} onClick={() => { if (!occupe) onDelete(); }}>Supprimer</Button>}
        <Button variant="secondary" size="sm" onClick={onClose}>{readOnly ? 'Fermer' : 'Annuler'}</Button>
        {!readOnly && <Button ref={valider} variant="primary" size="sm" aria-disabled={occupe} onClick={enregistrer}>{creneau == null ? 'Ajouter' : 'Enregistrer'}</Button>}
      </>}
    >
      <div className="py-1" onKeyDown={auClavier}><FormulaireCreneau {...formulaire} /></div>
      {error !== '' && <p role="alert" className="mt-3 rounded-sm border border-danger-border bg-danger-tint px-3 py-2 text-sm font-semibold text-danger">{error}</p>}
    </Modal>
  );
}
